/**
 * ComparisonRisks — column-per-site card strip showing each site's risk
 * profile at a glance:
 *
 *   ┌─────────────────────┐
 *   │ Site A               │
 *   │ Total: 12            │
 *   │ ┌──────┬──────┬─────┐│  severity counts
 *   │ │ HIGH │ MED  │ LOW ││  (using G1 HeatMap rules:
 *   │ │  3   │  5   │  4  ││   ≥16 high, 5–15 med, ≤4 low)
 *   │ └──────┴──────┴─────┘│
 *   │ Open: 7 · Mit: 2 ·   │  status row
 *   │ Acc: 1 · Res: 2      │
 *   │                      │
 *   │ Top 3 unresolved:    │
 *   │  1. Lease delay  ⚠ 25│
 *   │  2. Permitting   ⚠ 16│
 *   │  3. Construction ⚠ 12│
 *   └─────────────────────┘
 *
 * Sites with no risks render an empty-state card. Cards lay out as a
 * horizontal scroll strip when there are 4+ selected sites; otherwise an
 * equal-flex row at lg+.
 *
 * Linear: MYD-220 (F5).
 */

import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card } from 'components/ui/card'
import { AlertTriangle } from 'lucide-react'
import { cn } from 'lib/utils'
import { fetchRisksComparison } from 'slices/riskSlice'

interface ComparisonRisksProps {
	evalId: string
	sites: any[]
}

const STATUS_LABELS: Record<string, string> = {
	OPEN: 'Open',
	MITIGATING: 'Mit',
	ACCEPTED: 'Acc',
	RESOLVED: 'Res',
}

const STATUS_COLORS: Record<string, string> = {
	OPEN: 'text-red-700',
	MITIGATING: 'text-amber-700',
	ACCEPTED: 'text-blue-700',
	RESOLVED: 'text-emerald-700',
}

const severityBucket = (score: number): 'HIGH' | 'MEDIUM' | 'LOW' => {
	if (score >= 16) return 'HIGH'
	if (score <= 4) return 'LOW'
	return 'MEDIUM'
}

const ComparisonRisks = ({ evalId, sites }: ComparisonRisksProps) => {
	const dispatch = useDispatch()
	const comparison: any[] = useSelector(
		(state: any) => state.risks?.comparisonByEvaluation?.[evalId] || [],
	)

	useEffect(() => {
		;(dispatch as any)(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	const compareBySiteId = useMemo(() => {
		const out: Record<string, any> = {}
		for (const row of comparison) out[String(row.siteId)] = row
		return out
	}, [comparison])

	const useScrollStrip = sites.length >= 4

	return (
		<div
			className={
				useScrollStrip
					? 'flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 lg:mx-0 lg:px-0'
					: 'flex gap-3 overflow-x-auto lg:overflow-x-visible pb-2 -mx-1 px-1 lg:mx-0 lg:px-0'
			}
		>
			{sites.map((site) => {
				const row = compareBySiteId[site._id]
				const risks: any[] = row?.risks ?? []
				const total = risks.length
				const sev = { HIGH: 0, MEDIUM: 0, LOW: 0 }
				const status: Record<string, number> = {
					OPEN: 0,
					MITIGATING: 0,
					ACCEPTED: 0,
					RESOLVED: 0,
				}
				for (const r of risks) {
					sev[severityBucket(r.riskScore ?? 0)]++
					if (status[r.status] != null) status[r.status]++
				}
				const topUnresolved = risks
					.filter((r) => r.status === 'OPEN' || r.status === 'MITIGATING')
					.slice()
					.sort((a, b) => (b.riskScore ?? 0) - (a.riskScore ?? 0))
					.slice(0, 3)

				return (
					<Card
						key={site._id}
						className="flex flex-col w-[300px] shrink-0 lg:w-auto lg:shrink lg:flex-1 p-3 gap-2"
					>
						<div className="border-b border-gray-100 pb-2">
							<h4
								className="text-sm font-semibold text-gray-900 truncate"
								title={site.name}
							>
								{site.name}
							</h4>
							<div className="flex items-baseline gap-1.5 mt-1">
								<span className="text-2xl font-bold text-gray-900">{total}</span>
								<span className="text-[11px] uppercase tracking-wide text-gray-500">
									{total === 1 ? 'risk' : 'risks'}
								</span>
							</div>
						</div>

						{total === 0 ? (
							<div className="py-3 text-center text-xs text-gray-400">
								No risks logged for this site.
							</div>
						) : (
							<>
								{/* Severity */}
								<div className="grid grid-cols-3 gap-1.5">
									<SeverityTile label="HIGH" count={sev.HIGH} tone="red" />
									<SeverityTile label="MED" count={sev.MEDIUM} tone="amber" />
									<SeverityTile label="LOW" count={sev.LOW} tone="gray" />
								</div>

								{/* Status row */}
								<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-600 mt-1">
									{Object.keys(STATUS_LABELS).map((key) => (
										<span key={key}>
											<span
												className={cn(
													'font-semibold',
													STATUS_COLORS[key],
													status[key] === 0 && 'text-gray-400',
												)}
											>
												{STATUS_LABELS[key]}
											</span>
											: {status[key]}
										</span>
									))}
								</div>

								{/* Top 3 unresolved */}
								<div className="mt-1 pt-2 border-t border-gray-100">
									<div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
										Top unresolved
									</div>
									{topUnresolved.length === 0 ? (
										<p className="text-xs text-gray-400">No open or mitigating risks.</p>
									) : (
										<ol className="space-y-1">
											{topUnresolved.map((r, idx) => (
												<li
													key={r._id}
													className="flex items-center gap-1.5 text-xs text-gray-700"
												>
													<span className="w-4 text-[10px] font-semibold text-gray-400 shrink-0">
														{idx + 1}.
													</span>
													<span className="flex-1 truncate" title={r.name}>
														{r.name}
													</span>
													<span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-gray-600 shrink-0">
														<AlertTriangle className="w-3 h-3 text-amber-500" />
														{r.riskScore}
													</span>
												</li>
											))}
										</ol>
									)}
								</div>
							</>
						)}
					</Card>
				)
			})}
		</div>
	)
}

interface SeverityTileProps {
	label: string
	count: number
	tone: 'red' | 'amber' | 'gray'
}

const TONE_CLASSES: Record<SeverityTileProps['tone'], string> = {
	red: 'bg-red-50 border-red-200 text-red-700',
	amber: 'bg-amber-50 border-amber-200 text-amber-700',
	gray: 'bg-gray-50 border-gray-200 text-gray-600',
}

const SeverityTile = ({ label, count, tone }: SeverityTileProps) => (
	<div
		className={cn(
			'flex flex-col items-center justify-center rounded-md border px-1 py-1.5',
			TONE_CLASSES[tone],
		)}
	>
		<span className="text-base font-bold leading-tight">{count}</span>
		<span className="text-[9px] uppercase tracking-wider mt-0.5">{label}</span>
	</div>
)

export default ComparisonRisks
